import { Box } from '@chakra-ui/react';
import { SectionHeading } from '@/ui/SectionHeading';

interface PillSectionHeaderProps {
  title?: string;
  /**
   * Supplied by CollapseSection and deliberately ignored, along with `isOpen`.
   * The pill is a group heading, not a control: the design shows no chevron and
   * every group is open, so a click target here only gives the reader a way to
   * hide fields by accident with nothing indicating how to get them back.
   */
  onClick?: () => void;
  isOpen?: boolean;
  /**
   * The section's "Add X" action. It belongs on the header row, to the RIGHT of
   * the rule — not stacked under the pill as a body field. Omitted when the
   * section cannot take another entry (a job already fixed by the route, a
   * flow/project that allows only one, socials at their cap).
   */
  headerAction?: { text: string; onClick: () => void };
}

/**
 * The create-candidate drawer's group header: a small pill followed by a rule
 * that fills the row.
 *
 * `SectionHeading` already IS this treatment — it is what settings/candidate-
 * fields renders above "Default fields" — so the drawer reuses it rather than
 * growing a second pill. The design (Candidate Page, node 2070:25097) gives
 * every group the same header, which is why this is passed as CollapseSection's
 * `HeaderComp` instead of being wired into one call site.
 *
 * Deliberately no chevron and not collapsible: the design shows neither, and
 * every group opens by default.
 */
export const PillSectionHeader = ({
  title,
  headerAction,
}: PillSectionHeaderProps) => (
  // The pill is full-bleed in the drawer body, which insets its own content —
  // so the header cancels that inset rather than sitting indented from it.
  <Box px="2em">
    <SectionHeading
      title={title ?? ''}
      // The design sizes both the pill and its Add button at 32px. Passed from
      // here rather than changed in SectionHeading: 27 other callers render
      // that heading at its current size, and this design covers the drawer.
      height="32px"
      display="flex"
      alignItems="center"
      // className, not a `height` prop: this Button is a kula-ai component and
      // ignores Chakra style props -- the `p="9px 12px !important"` already on
      // it resolves to `0px 8px`, so it never applied either.
      addButtonProps={{ className: '!h-8' }}
      headingProps={{
        // No bottom margin: the first field below carries its own top spacing,
        // and stacking 16px on top of it pushed the group open. SectionHeading
        // defaults to mb="12px", so this has to be set, not omitted.
        mb: 0,
      }}
      showAddButton={Boolean(headerAction)}
      addButtonText={headerAction?.text}
      // Still stopped: CollapseSection's own handler sits on the row wrapping
      // this header, so without it an Add click would also toggle the group.
      onAddClick={(event?: { stopPropagation?: () => void }) => {
        event?.stopPropagation?.();
        headerAction?.onClick();
      }}
    />
  </Box>
);
